import React, { useState, useEffect, useContext } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { AppContext } from "../../../../../context/AppContext";
import usePermissions from "../../../../../hooks/usePermissions";
import Loading from "../../../../../utils/Loading";
import Table, { TableColumns } from "../../../../Shared/Table/Table";
import { Button } from "../../../../Shared/Buttons/Button/Button";
import { EDSButton } from "../../../../eds/EDSButton";
import { AlertBanner } from "@/components/blocks/alert-banner";
import { Trash } from "lucide-react";
import { SelectedBookingTabEnum } from "../Tabs/SelectedBookingTabsController";
import {
  getLinkedBookingDetails,
  getPaymentsDataAll,
  GetPaymentsDataReturnType,
  handleClaimPayment,
  handleRefundPayment
} from "./SelectedBookingPaymentsController";
import {
  addDeposit,
  deleteDeposit,
  getBookingSignature
} from "../../../../../APIs/Booking/PaymentOperations";
import AddPaymentModal, { PaymentFormData } from "./AddPaymentModal";
import RefundModal, { RefundFormData } from "./RefundModal";
import ClaimModal, { ClaimFormData } from "./ClaimModal";
import {
  canClaimDeposit,
  canRefundDeposit,
  isPaymentWindowExpired
} from "./PaymentEligibility";
import { getEnvironment } from "../../../../../utils/environment";
import { openExternalLink } from "../../../../../utils/nativeUtils/openExternalLink";
import InvoicesSection from "../Invoices/InvoicesSection";
import { useFeatureFlagContext } from "../../../../../hooks/useFeatureFlagContext";

type SelectedBookingPaymentsProps = {
  // set when called from linked tab to show all linked payments
  linkedBookingIds?: string[];
  setSelectedTabKey?: (value: SelectedBookingTabEnum) => void;
  initialPaymentAmount?: number;
  onInitialAmountUsed?: () => void;
};
const SelectedBookingPayments = ({
  linkedBookingIds,
  setSelectedTabKey,
  initialPaymentAmount,
  onInitialAmountUsed
}: SelectedBookingPaymentsProps) => {
  const diaryContext = useDiaryContext();
  const { isFlagEnabled } = useFeatureFlagContext();
  const isInvoicingEnabled = isFlagEnabled("enableInvoicing");
  const { venueGroupConfig } = useContext(AppContext);
  const { connectionStatus } = useDeviceContext();
  const permissions = usePermissions();
  const queryClient = useQueryClient();

  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedDeposit, setSelectedDeposit] = useState<any>(null);
  const [paymentLink, setPaymentLink] = useState<string>("");
  const [isPaymentLinkExpired, setIsPaymentLinkExpired] =
    useState<boolean>(false);
  const currency =
    diaryContext.selectedBookingV4?.deposits?.[0]?.currency ?? "GBP";

  useEffect(() => {
    if (initialPaymentAmount !== undefined && initialPaymentAmount > 0) {
      setShowAddPaymentModal(true);
    }
  }, [initialPaymentAmount]);

  useEffect(() => {
    const generatePaymentLink = async () => {
      if (!diaryContext.selectedBookingV4?._id) {
        setPaymentLink("");
        return;
      }

      const hasPendingSaved = diaryContext.selectedBookingV4.deposits?.some(
        (deposit) => deposit.status !== "paid" && deposit.id
      );

      if (!hasPendingSaved) {
        setPaymentLink("");
        return;
      }

      try {
        const env = await getEnvironment();
        const bookingId = diaryContext.selectedBookingV4._id;
        const hasNewGuestExperience =
          venueGroupConfig?.collins_features?.new_guest_experience;

        let link: string;

        if (hasNewGuestExperience) {
          const newCheckoutUrl = env?.NEW_CHECKOUT_URL ?? "";
          const signature = await getBookingSignature(bookingId);
          link = signature
            ? `${newCheckoutUrl}/secure-your-booking?booking_id=${bookingId}&signature=${signature}`
            : `${newCheckoutUrl}/secure-your-booking?booking_id=${bookingId}`;
        } else {
          const baseBookingsUrl = env?.BASE_BOOKINGS_URL ?? "";
          link = `${baseBookingsUrl}/secure-your-booking?booking_id=${bookingId}`;
        }

        setPaymentLink(link);

        if (
          venueGroupConfig?.payments_auto_close_configuration &&
          diaryContext.selectedBookingV4.date
        ) {
          const expired = isPaymentWindowExpired(
            diaryContext.selectedBookingV4.date,
            venueGroupConfig.payments_auto_close_configuration
          );
          setIsPaymentLinkExpired(expired);
        } else {
          setIsPaymentLinkExpired(false);
        }
      } catch (error) {
        console.error("Failed to generate payment link:", error);
        setPaymentLink("");
      }
    };

    generatePaymentLink();
  }, [
    diaryContext.selectedBookingV4?._id,
    diaryContext.selectedBookingV4?.deposits,
    diaryContext.selectedBookingV4?.date,
    venueGroupConfig?.payments_auto_close_configuration,
    venueGroupConfig?.collins_features?.new_guest_experience
  ]);

  const { data: linkedBookingsData, isLoading } = useQuery({
    queryKey: ["linkedBookingPaymentDetailsData", linkedBookingIds?.toString()],
    queryFn: () =>
      getLinkedBookingDetails({
        linkedBookingIds,
        selectedBookingV4: diaryContext.selectedBookingV4
      })
  });

  const totalClaimsCount =
    diaryContext.selectedBookingV4?.deposits?.reduce(
      (sum, dep) => sum + (dep.claims?.length ?? 0),
      0
    ) ?? 0;

  const totalRefundsCount =
    diaryContext.selectedBookingV4?.refunds?.length ?? 0;

  const { data, isLoading: isLoading2 } = useQuery({
    queryKey: [
      "BookingPaymentData",
      linkedBookingsData?.length,
      diaryContext.selectedBookingV4?._id,
      diaryContext.selectedBookingV4?.deposits?.length,
      totalClaimsCount,
      totalRefundsCount
    ],
    queryFn: () =>
      getPaymentsDataAll({
        linkedBookingsData,
        isLoading,
        diaryContext,
        linkedBookingIds,
        setSelectedTabKey
      })
  });

  const { bookingPayments, amountTotal } = data ?? {
    bookingPayments: [],
    amountTotal: 0
  };

  const canCreateDeposits = true;

  const canManageDeposits = permissions.roles.booking.deposits.update;

  const canManageRefunds = permissions.roles.booking.refunds?.create;

  const canDeleteDepositCheck = (deposit: any) => {
    return (
      permissions.roles.booking.deposits.update &&
      deposit.type !== "card_authorised" &&
      deposit.status !== "paid"
    );
  };

  const bookingPaymentsWithActions = bookingPayments.map((payment) => {
    if (payment.rowType !== "parent") {
      const nestedClass =
        payment.rowType === "claim"
          ? "BookingPayments-NestedRow BookingPayments-ClaimRow"
          : "BookingPayments-NestedRow BookingPayments-RefundRow";

      return {
        ...payment,
        displayDate: <span className={nestedClass}>{payment.displayDate}</span>,
        displayType: (
          <span
            className={nestedClass}
            style={{
              color:
                payment.rowType === "claim"
                  ? "var(--successColor, #28a745)"
                  : "var(--errorColor, #dc3545)",
              fontWeight: 500
            }}
          >
            {payment.displayType}
          </span>
        )
      };
    }

    const showClaim = canManageDeposits && canClaimDeposit(payment);
    const showRefund =
      canManageRefunds && diaryContext.selectedBookingV4
        ? canRefundDeposit(payment, diaryContext.selectedBookingV4)
        : false;
    const showDelete = canDeleteDepositCheck(payment);

    return {
      ...payment,
      actions: (
        <div className="BookingPayments-Actions flex gap-2 justify-center items-center flex-wrap">
          {showClaim && (
            <EDSButton
              onClick={() => {
                setSelectedDeposit(payment);
                setShowClaimModal(true);
              }}
              variant="outline"
              size="sm"
            >
              Claim
            </EDSButton>
          )}
          {showRefund && (
            <EDSButton
              onClick={() => {
                setSelectedDeposit(payment);
                setShowRefundModal(true);
              }}
              variant="outline"
              size="sm"
            >
              Refund
            </EDSButton>
          )}
          {showDelete && (
            <EDSButton
              onClick={() => handleDeleteDeposit(payment.id)}
              size="icon"
              variant="outline"
              aria-label="Delete payment"
            >
              <Trash className="w-4 h-4" />
            </EDSButton>
          )}
        </div>
      )
    };
  });

  const handleAddPaymentSubmit = async (paymentData: PaymentFormData) => {
    if (!diaryContext.selectedBookingV4) return;

    const result = await addDeposit(
      diaryContext.selectedBookingV4,
      paymentData,
      diaryContext
    );

    if (result?.booking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: result.booking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });
  };

  const handleRefundSubmit = async (refundData: RefundFormData) => {
    if (!diaryContext.selectedBookingV4 || !selectedDeposit) return;

    const updatedBooking = await handleRefundPayment(
      diaryContext.selectedBookingV4,
      selectedDeposit.id,
      refundData
    );

    if (updatedBooking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: updatedBooking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["BookingPaymentData"] });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });

    setShowRefundModal(false);
    setSelectedDeposit(null);
  };

  const handleClaimSubmit = async (claimData: ClaimFormData) => {
    if (!diaryContext.selectedBookingV4 || !selectedDeposit) return;

    const updatedBooking = await handleClaimPayment(
      diaryContext.selectedBookingV4,
      selectedDeposit.id,
      claimData.amount
    );

    if (updatedBooking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: updatedBooking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["BookingPaymentData"] });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });

    setShowClaimModal(false);
    setSelectedDeposit(null);
  };

  const handleDeleteDeposit = async (depositId: string) => {
    if (!diaryContext.selectedBookingV4) return;

    const updatedBooking = await deleteDeposit(
      diaryContext.selectedBookingV4,
      depositId,
      diaryContext
    );

    if (updatedBooking) {
      diaryContext.setDiaryContext({
        selectedBookingV4: updatedBooking
      });
    }

    queryClient.invalidateQueries({
      queryKey: ["linkedBookingPaymentDetailsData"]
    });
    queryClient.invalidateQueries({ queryKey: ["BookingPaymentData"] });
    queryClient.invalidateQueries({ queryKey: ["getSelectedBooking"] });
  };

  const loading =
    !diaryContext.selectedBookingV4 ||
    ((isLoading || isLoading2) && connectionStatus === "internet");

  const heading = `${linkedBookingIds ? "Linked " : ""}Payments`;
  const noRecords = linkedBookingIds
    ? "No linked payments found."
    : "No payments have been added to this booking";
  const [paymentNoteCount, setPaymentNoteCount] = useState<number>(0);

  useEffect(() => {
    // this is to fix a weird issue with tagPopover not showing for the notes button.
    let count = 0;
    bookingPayments.forEach((row: GetPaymentsDataReturnType) => {
      if (row.displayNote) {
        ++count;
      }
    });
    setTimeout(() => setPaymentNoteCount(count), 100);
  }, [bookingPayments]);

  const columns: TableColumns<GetPaymentsDataReturnType>[] = [
    {
      dataNameJSX: "displayDate",
      caption: "Added"
    },
    {
      dataNameJSX: "displayType",
      caption: "Type"
    },
    {
      dataName: "displayAmount",
      caption: "Amount",
      total:
        bookingPayments.length > 0
          ? amountTotal.toLocaleString(undefined, {
              style: "currency",
              currency
            })
          : undefined,
      className: "BookingPayments-TableColumnAmount",
      type: "number"
    },
    {
      dataName: "displayPaidBy",
      caption: "Paid by",
      className: "BookingPayments-TableColumnPaidBy"
    },
    {
      dataName: "displayCard",
      caption: "Card",
      className: "BookingPayments-TableColumnCard"
    },
    {
      dataNameJSX: "displayStatus",
      caption: "Status",
      className: "BookingPayments-TableColumnStatus"
    },
    {
      dataNameJSX: "displayNote",
      caption: "Notes",
      className: "BookingPayments-TableColumnDisplayNote",
      align: "Center"
    },
    {
      dataNameJSX: "actions",
      caption: "Actions",
      className: "BookingPayments-TableColumnActions",
      align: "Center"
    }
  ];

  if (linkedBookingIds) {
    // add reference column for linked payments view, only
    columns.unshift({
      caption: "DMN Ref #",
      dataNameJSX: "reference"
    });
  }

  return (
    <div className="BookingPayments mt-[var(--panelPadding)] flex flex-col gap-4 flex-1 min-h-0">
      <div className="BookingPayments-ViewSection bg-[var(--panelBackground)] rounded-lg shadow-sm p-[var(--panelPadding)] flex-1 flex flex-col min-h-0">
        <div className="BookingPayments-Heading font-bold text-[length:var(--fontSize-base)] mb-[var(--panelPadding)]">
          {heading}
        </div>
        {paymentLink && (
          <AlertBanner
            variant="info"
            title="Card details link"
            description={
              <span>
                <a
                  href={paymentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="BookingPayments-PaymentLink !text-[#1fd4d4] no-underline break-all font-medium inline hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    openExternalLink(paymentLink);
                  }}
                >
                  {paymentLink}
                </a>
                {isPaymentLinkExpired && (
                  <span className="BookingPayments-ExpiredLabel bg-[var(--errorColor,#dc3545)] text-white px-2 py-0.5 rounded-[calc(var(--radius)-2px)] text-[11px] font-semibold uppercase ml-2 inline-block align-middle">
                    {" "}
                    expired
                  </span>
                )}
              </span>
            }
            dismissible={false}
            className="BookingPayments-PaymentLinkAlert mb-4"
          />
        )}
        <div className="BookingPayments-Content BookingPayments-TableWrapper flex flex-col flex-1 overflow-y-auto min-h-0">
          <Table
            key={`${diaryContext.selectedBookingV4?._id}-${paymentNoteCount}`}
            columns={columns}
            data={bookingPaymentsWithActions}
          />
          {loading && <Loading />}
          {bookingPayments.length === 0 && !loading && (
            <span className="NoRecords">{noRecords}</span>
          )}
        </div>
      </div>

      {!linkedBookingIds && canCreateDeposits && (
        <div className="BookingPayments-AddSection bg-[var(--panelBackground)] rounded-lg shadow-sm p-[var(--panelPadding)] flex justify-center">
          <Button onClick={() => setShowAddPaymentModal(true)} type="primary">
            Add Payment
          </Button>
        </div>
      )}

      {showAddPaymentModal && diaryContext.selectedBookingV4 && (
        <AddPaymentModal
          booking={diaryContext.selectedBookingV4}
          onClose={() => {
            setShowAddPaymentModal(false);
            onInitialAmountUsed?.();
          }}
          onSubmit={handleAddPaymentSubmit}
          initialAmount={initialPaymentAmount}
        />
      )}

      {showRefundModal && diaryContext.selectedBookingV4 && selectedDeposit && (
        <RefundModal
          deposit={selectedDeposit}
          booking={diaryContext.selectedBookingV4}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedDeposit(null);
          }}
          onSubmit={handleRefundSubmit}
        />
      )}

      {showClaimModal && diaryContext.selectedBookingV4 && selectedDeposit && (
        <ClaimModal
          deposit={selectedDeposit}
          onClose={() => {
            setShowClaimModal(false);
            setSelectedDeposit(null);
          }}
          onSubmit={handleClaimSubmit}
        />
      )}

      {!linkedBookingIds && isInvoicingEnabled && <InvoicesSection />}
    </div>
  );
};

export default SelectedBookingPayments;
